import type { Applicant, Application, Employee, JobOpening, Offer } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Clock, Copy, DollarSign, Eye, Send, Trash2, User, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { OffersKanbanView } from "@/components/offers-kanban-view";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency, formatSharesRange } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type OfferWithDetails = Offer & {
  application?: Application & { applicant?: Applicant; jobOpening?: JobOpening };
  creator?: Employee;
  approver?: Employee;
};

// Status labels rendered via <StatusBadge /> word-carries-state (§10.9).
// All keys covered in client/src/components/status-badge.tsx STATUS_MAP.

function OfferCard({
  offer,
  borderClass,
  extraInfo,
  actions,
  copyOfferLink,
  viewPreview,
  onDelete,
}: {
  offer: OfferWithDetails;
  borderClass?: string;
  extraInfo?: ReactNode;
  actions?: ReactNode;
  copyOfferLink: (token: string) => void;
  viewPreview: (token: string) => void;
  onDelete: (offer: OfferWithDetails) => void;
}) {
  const salaryChanged =
    offer.acceptedSalaryBase != null &&
    Math.abs(parseFloat(offer.acceptedSalaryBase) - parseFloat(offer.salaryBase)) > 0.01;
  const equityChanged =
    offer.acceptedEquityShares != null && offer.acceptedEquityShares !== (offer.equityShares ?? 0);

  return (
    <Card data-testid={`card-offer-${offer.id}`} className={borderClass || ""}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4" />
            {offer.application?.applicant?.fullName || "Candidato"}
          </CardTitle>
          <div className="flex items-center gap-2">
            <StatusBadge status={offer.status} />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--alert-red))]"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(offer);
              }}
              data-testid={`button-delete-offer-${offer.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Vaga</p>
            <p className="font-medium">{offer.application?.jobOpening?.title || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Salário Base
            </p>
            {salaryChanged ? (
              <div>
                <p className="font-medium">
                  {formatCurrency(parseFloat(offer.acceptedSalaryBase!))}/mês
                </p>
                <p className="text-xs text-muted-foreground line-through">
                  {formatCurrency(parseFloat(offer.salaryBase))}/mês
                </p>
              </div>
            ) : (
              <p className="font-medium">{formatCurrency(parseFloat(offer.salaryBase))}/mês</p>
            )}
          </div>
          {(offer.equityShares || offer.acceptedEquityShares) && (
            <div>
              <p className="text-muted-foreground">Equity</p>
              {equityChanged ? (
                <div>
                  <p className="font-medium">
                    {offer.acceptedEquityShares!.toLocaleString("pt-BR")} shares
                  </p>
                  <p className="text-xs text-muted-foreground line-through">
                    {(offer.equityShares ?? 0).toLocaleString("pt-BR")} shares
                  </p>
                </div>
              ) : (
                <p className="font-medium">{offer.equityShares ?? 0} shares</p>
              )}
            </div>
          )}
          {offer.signingBonus && (
            <div>
              <p className="text-muted-foreground">Signing Bonus</p>
              <p className="font-medium">{formatCurrency(parseFloat(offer.signingBonus))}</p>
            </div>
          )}
          {offer.performanceBonus && (
            <div>
              <p className="text-muted-foreground">Bônus de Performance</p>
              <p className="font-medium">{formatCurrency(parseFloat(offer.performanceBonus))}</p>
            </div>
          )}
          {offer.performanceBonusShares && (
            <div>
              <p className="text-muted-foreground">Bônus de Performance (Shares)</p>
              <p className="font-medium">
                {formatSharesRange(offer.performanceBonusShares, offer.performanceBonusSharesMax)}
              </p>
            </div>
          )}
          {extraInfo}
        </div>
        {actions}
        {!actions && offer.acceptanceToken && (
          <div className="flex gap-2 pt-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                copyOfferLink(offer.acceptanceToken);
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
            {offer.previewToken && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  viewPreview(offer.previewToken!);
                }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function OffersPage() {
  const { toast } = useToast();
  const [selectedOffer, setSelectedOffer] = useState<OfferWithDetails | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [sendConfirmOffer, setSendConfirmOffer] = useState<OfferWithDetails | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [deleteConfirmOffer, setDeleteConfirmOffer] = useState<OfferWithDetails | null>(null);
  const [acceptConfirmOffer, setAcceptConfirmOffer] = useState<OfferWithDetails | null>(null);
  const [startDate, setStartDate] = useState("");

  const invalidateOfferKanban = () => {
    const jobId = selectedOffer?.application?.jobOpeningId;
    if (jobId) {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId: jobId } })
          .queryKey,
      });
    }
  };

  const { data: offers, isLoading } = useQuery(orpc.offers.list.queryOptions()) as {
    data: OfferWithDetails[] | undefined;
    isLoading: boolean;
  };

  const approveMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.approve({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      invalidateOfferKanban();
      toast({
        title: "Oferta aprovada",
        description: "O candidato receberá um email com a oferta.",
      });
      closeDialog();
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível aprovar a oferta.",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({
      offerId,
      rejectionReason,
    }: {
      offerId: string;
      rejectionReason: string;
    }) => {
      return rpcClient.offers.reject({ id: offerId, rejectionReason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      invalidateOfferKanban();
      toast({
        title: "Oferta recusada",
        description: "O hiring manager será notificado.",
      });
      closeDialog();
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível recusar a oferta.",
        variant: "destructive",
      });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.sendToCandidate({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      invalidateOfferKanban();
      toast({
        title: "Oferta enviada",
        description: "O candidato receberá um email com a oferta.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível enviar a oferta.",
        variant: "destructive",
      });
    },
  });

  const markAsAcceptedMutation = useMutation({
    mutationFn: async ({ offerId, startDate }: { offerId: string; startDate: string }) => {
      return rpcClient.offers.markAsAccepted({ id: offerId, startDate });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      invalidateOfferKanban();
      toast({
        title: "Oferta aceita!",
        description: "Candidato convertido em colaborador.",
      });
      setAcceptConfirmOffer(null);
      setStartDate("");
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível marcar como aceita.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.delete({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      toast({
        title: "Oferta excluída",
        description: "A oferta foi removida com sucesso.",
      });
      setDeleteConfirmOffer(null);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível excluir a oferta.",
        variant: "destructive",
      });
    },
  });

  const closeDialog = () => {
    setSelectedOffer(null);
    setActionType(null);
    setRejectionReason("");
  };

  const handleAction = (offer: OfferWithDetails, action: "approve" | "reject") => {
    setSelectedOffer(offer);
    setActionType(action);
  };

  const confirmAction = () => {
    if (!selectedOffer || !actionType) return;

    if (actionType === "approve") {
      approveMutation.mutate(selectedOffer.id);
    } else {
      // Validar justificativa obrigatória
      if (!rejectionReason.trim()) {
        toast({
          title: "Justificativa obrigatória",
          description: "Por favor, informe o motivo da recusa.",
          variant: "destructive",
        });
        return;
      }
      rejectMutation.mutate({ offerId: selectedOffer.id, rejectionReason: rejectionReason.trim() });
    }
  };

  const copyOfferLink = (token: string) => {
    const link = `${window.location.origin}/offer/${token}`;
    navigator.clipboard.writeText(link);
    toast({
      title: "Link copiado!",
      description: "O link da oferta foi copiado para a área de transferência.",
    });
  };

  const viewPreview = (token: string) => {
    window.open(`/offer-preview/${token}`, "_blank");
  };

  const pendingOffers = offers?.filter((o) => o.status === "waiting_approval") || [];
  const approvedOffers = offers?.filter((o) => o.status === "approved") || [];
  const sentOffers = offers?.filter((o) => o.status === "sent") || [];
  const acceptedOffers = offers?.filter((o) => o.status === "accepted") || [];
  const rejectedOffers =
    offers?.filter((o) => ["rejected", "declined", "cancelled"].includes(o.status)) || [];

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        {["a", "b", "c", "d", "e"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sand-divider))]" />
            <span className="h-4 flex-1 max-w-md rounded bg-[hsl(var(--sand-divider))]" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Ofertas" description="Gerencie ofertas de emprego" />

      <Tabs
        value={viewMode}
        onValueChange={(v) => setViewMode(v as "list" | "kanban")}
        className="w-auto"
      >
        <TabsList className="justify-start w-auto">
          <TabsTrigger value="list" data-testid="tab-list-view">
            Lista
          </TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban-view">
            Kanban
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {viewMode === "kanban" && offers && (
        <OffersKanbanView
          offers={offers}
          onApprove={(offer) => handleAction(offer, "approve")}
          onReject={(offer) => handleAction(offer, "reject")}
          onSend={(offer) => setSendConfirmOffer(offer)}
          onDelete={(offer) => setDeleteConfirmOffer(offer)}
          onMarkAccepted={(offer) => setAcceptConfirmOffer(offer)}
          copyOfferLink={copyOfferLink}
          viewPreview={viewPreview}
        />
      )}

      {viewMode === "list" && pendingOffers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-[hsl(var(--amber-state))]" />
            Aguardando aprovação ({pendingOffers.length})
          </h2>
          <div className="grid gap-4">
            {pendingOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                borderClass="border-l-4 border-l-[hsl(var(--amber-state))] bg-[hsl(var(--amber-mist))]"
                copyOfferLink={copyOfferLink}
                viewPreview={viewPreview}
                onDelete={(o) => setDeleteConfirmOffer(o)}
                extraInfo={
                  <div>
                    <p className="text-[hsl(var(--ink-muted))]">Criado por</p>
                    <p className="font-medium">{offer.creator?.fullName || "-"}</p>
                  </div>
                }
                actions={
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => handleAction(offer, "approve")}
                      className="flex-1"
                      data-testid={`button-approve-offer-${offer.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Aprovar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleAction(offer, "reject")}
                      className="flex-1"
                      data-testid={`button-reject-offer-${offer.id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Recusar
                    </Button>
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && approvedOffers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Check className="w-5 h-5 text-[hsl(var(--forest-pulse))]" />
            Ofertas aprovadas ({approvedOffers.length})
          </h2>
          <div className="grid gap-4">
            {approvedOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                borderClass="border-l-4 border-l-[hsl(var(--forest-pulse))] bg-[hsl(var(--forest-mist))]"
                copyOfferLink={copyOfferLink}
                viewPreview={viewPreview}
                onDelete={(o) => setDeleteConfirmOffer(o)}
                extraInfo={
                  <div>
                    <p className="text-[hsl(var(--ink-muted))]">Aprovado por</p>
                    <p className="font-medium">{offer.approver?.fullName || "-"}</p>
                  </div>
                }
                actions={
                  <div className="flex gap-2 pt-2 flex-wrap">
                    {offer.previewToken && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewPreview(offer.previewToken!)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Visualizar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyOfferLink(offer.acceptanceToken)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copiar link
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={() => setSendConfirmOffer(offer)}
                      disabled={sendMutation.isPending}
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Enviar oferta
                    </Button>
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && sentOffers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Send className="w-5 h-5 text-[hsl(var(--amber-state))]" />
            Enviadas ({sentOffers.length})
          </h2>
          <div className="grid gap-4">
            {sentOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                borderClass="border-l-4 border-l-[hsl(var(--amber-state))] bg-[hsl(var(--amber-mist))]"
                copyOfferLink={copyOfferLink}
                viewPreview={viewPreview}
                onDelete={(o) => setDeleteConfirmOffer(o)}
                actions={
                  <div className="flex gap-2 pt-2 flex-wrap">
                    {offer.acceptanceToken && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyOfferLink(offer.acceptanceToken)}
                      >
                        <Copy className="w-4 h-4 mr-2" /> Copiar link
                      </Button>
                    )}
                    {offer.previewToken && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewPreview(offer.previewToken!)}
                      >
                        <Eye className="w-4 h-4 mr-2" /> Preview
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => setAcceptConfirmOffer(offer)}
                      data-testid={`button-mark-accepted-offer-${offer.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" /> Marcar como aceita
                    </Button>
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && acceptedOffers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Check className="w-5 h-5 text-[hsl(var(--forest-pulse))]" />
            Aceitas ({acceptedOffers.length})
          </h2>
          <div className="grid gap-4">
            {acceptedOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                borderClass="border-l-4 border-l-[hsl(var(--forest-pulse))] bg-[hsl(var(--forest-mist))]"
                copyOfferLink={copyOfferLink}
                viewPreview={viewPreview}
                onDelete={(o) => setDeleteConfirmOffer(o)}
              />
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && rejectedOffers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <X className="w-5 h-5 text-[hsl(var(--ink-muted))]" />
            Recusadas / Canceladas ({rejectedOffers.length})
          </h2>
          <div className="grid gap-4">
            {rejectedOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                borderClass="border-l-4 border-l-[hsl(var(--sand-divider))] bg-[hsl(var(--sand-step))]"
                copyOfferLink={copyOfferLink}
                viewPreview={viewPreview}
                onDelete={(o) => setDeleteConfirmOffer(o)}
              />
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && (!offers || offers.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-[hsl(var(--ink-muted))]">
            <DollarSign className="w-12 h-12 mb-4 opacity-50" />
            <p>Sem ofertas ainda. Crie a primeira oferta para um candidato.</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={!!sendConfirmOffer}
        onOpenChange={(open) => !open && setSendConfirmOffer(null)}
      >
        <AlertDialogContent data-testid="dialog-confirm-send-offer">
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar oferta ao candidato?</AlertDialogTitle>
            <AlertDialogDescription>
              A oferta será enviada para o email{" "}
              <span className="font-semibold text-foreground">
                {sendConfirmOffer?.application?.applicant?.email || "não informado"}
              </span>
              . Deseja confirmar o envio?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-send-offer">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (sendConfirmOffer) {
                  sendMutation.mutate(sendConfirmOffer.id);
                }
                setSendConfirmOffer(null);
              }}
              disabled={sendMutation.isPending}
              data-testid="button-confirm-send-offer"
            >
              {sendMutation.isPending ? "Enviando..." : "Confirmar Envio"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteConfirmOffer}
        onOpenChange={(open) => !open && setDeleteConfirmOffer(null)}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-offer">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a oferta para{" "}
              <span className="font-semibold text-foreground">
                {deleteConfirmOffer?.application?.applicant?.fullName || "este candidato"}
              </span>
              ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-offer">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteConfirmOffer) {
                  deleteMutation.mutate(deleteConfirmOffer.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-offer"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!acceptConfirmOffer}
        onOpenChange={(open) => {
          if (!open) {
            setAcceptConfirmOffer(null);
            setStartDate("");
          }
        }}
      >
        <DialogContent data-testid="dialog-confirm-mark-accepted">
          <DialogHeader>
            <DialogTitle>Marcar oferta como aceita?</DialogTitle>
            <DialogDescription>
              Confirme a data de início de{" "}
              <span className="font-semibold text-foreground">
                {acceptConfirmOffer?.application?.applicant?.fullName}
              </span>{" "}
              para a vaga de{" "}
              <span className="font-semibold text-foreground">
                {acceptConfirmOffer?.application?.jobOpening?.title}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">
                Data de Início <span className="text-destructive">*</span>
              </Label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoFocus
                data-testid="input-start-date"
              />
              <p className="text-sm text-muted-foreground">
                Define o dia 1 do onboarding e o início do ciclo de performance.
              </p>
            </div>

            <div className="rounded-md bg-[hsl(var(--amber-mist))] border border-[hsl(var(--amber-state))]/30 p-3 text-sm text-[hsl(var(--ink-primary))]">
              <p className="font-medium mb-1">Esta ação é irreversível e irá:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Criar o registro do colaborador</li>
                <li>Mover a candidatura para "Contratado"</li>
                <li>Disparar o plano de onboarding</li>
                <li>Criar o primeiro ciclo de performance (3 meses)</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAcceptConfirmOffer(null);
                setStartDate("");
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (acceptConfirmOffer) {
                  markAsAcceptedMutation.mutate({
                    offerId: acceptConfirmOffer.id,
                    startDate,
                  });
                }
              }}
              disabled={markAsAcceptedMutation.isPending || !startDate}
              data-testid="button-confirm-mark-accepted"
            >
              {markAsAcceptedMutation.isPending ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" /> Salvando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" /> Confirmar aceite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedOffer} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Aprovar oferta" : "Recusar oferta"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve"
                ? `Deseja aprovar a oferta para ${selectedOffer?.application?.applicant?.fullName}? O candidato receberá um email com os detalhes da oferta e um link para aceitar.`
                : `Deseja recusar a oferta para ${selectedOffer?.application?.applicant?.fullName}? O hiring manager será notificado.`}
            </DialogDescription>
          </DialogHeader>

          {actionType === "reject" && (
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                Motivo da recusa <span className="text-[hsl(var(--alert-red))]">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Explique o motivo (ex: salário acima do orçamento, perfil não alinhado, etc.)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px]"
                autoFocus
              />
              <p className="text-sm text-muted-foreground">
                Esta justificativa será enviada ao hiring manager via Slack.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="button-cancel-action">
              Cancelar
            </Button>
            <Button
              variant={actionType === "reject" ? "destructive" : "default"}
              onClick={confirmAction}
              disabled={approveMutation.isPending || rejectMutation.isPending}
              data-testid="button-confirm-action"
            >
              {approveMutation.isPending || rejectMutation.isPending ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  {actionType === "approve" ? "Aprovando..." : "Recusando..."}
                </>
              ) : actionType === "approve" ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Aprovar oferta
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Recusar oferta
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
